import React, { useEffect, useState } from "react";
import ProtectedPage from "./ProtectedPage";
import Menu from "./Sidebar";
import TopBar from "./TopBar";
import { useAppDispatch, useAppSelector } from "@/redux/features/hooks";
import { notificationService } from "@/services/NotificationService";
import { NotificationsProvider } from "@/context/NotificationsContext";
import { Roles } from "@/redux/services/user/user.type";
import {
  addNotification,
  setNotifications,
} from "@/redux/slices/notification.slice";
import { useGetNotificationsQuery } from "@/redux/services/notification/notification.api";
import { useToggle } from "@/lib/hooks";
import SecurityErrorDialog from "@/pages/Errors/SecurityErrorDialog";
import { QueryError } from "@/lib/type";
import { useNotificationPermission } from "@/hooks/useNotificationPermission";
import { useRegisterSessionRealtime } from "@/hooks/useRegisterSessionRealtime";
import { useExpensesRealtime } from "@/hooks/useExpensesRealtime";
import {
  getNotificationAction,
  INVENTORY_NOTIFICATION_ACTIONS,
} from "@/lib/utils";
import Banner from "@/components/common/Banner";
import { playNotificationSound } from "@/lib/audioUtils";
import { wakeLockService } from "@/services/WakeLockService";
import { notificationPollingService } from "@/services/NotificationPollingService";
import { logger } from "@/services/Logger";
import { PanelLeftOpen } from "lucide-react";

const SIDEBAR_COLLAPSED_KEY = "dashLayout.sidebarCollapsed";

const handleSecurityError = (error: QueryError, open: () => void) => {
  if (
    error?.data?.message === "security.error.origin_header_not_found" ||
    error?.data?.message === "security.error.unauthorized_origin" ||
    error?.data?.message === "security.error.unauthorized_app_access"
  ) {
    open();
  }
};

const DashLayout = ({
  children,
  hideTopBar = false,
}: {
  children: React.ReactNode;
  hideTopBar?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const { isOpen, close, open } = useToggle();

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true"
  );

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  const collapseSidebar = () => setIsSidebarCollapsed(true);
  const expandSidebar = () => setIsSidebarCollapsed(false);

  const currentUser = useAppSelector((state) => state.user.currentUser);
  const currentRestaurant = useAppSelector(
    (state) => state.user.currentRestaurant
  );

  const { hasPermission, showPermissionDialog, permissionChecked } =
    useNotificationPermission();

  // Subscribe to register-session lifecycle events. When a manager opens or
  // closes a session from another device, every connected client of this
  // restaurant invalidates its cached session and refetches.
  useRegisterSessionRealtime(currentRestaurant?.id);

  // Keep the expense list fresh across devices when expenses are recorded/voided.
  useExpensesRealtime(currentRestaurant?.id);

  const isOwner = currentUser?.roles?.includes(Roles.owner);
  const hasSpecificRestaurant = !!currentRestaurant?.id;
  const skipNotifications = isOwner && !hasSpecificRestaurant;

  const { data: initialNotifications } = useGetNotificationsQuery(
    { query: { limit: 20, page: 1 } },
    { skip: skipNotifications }
  );

  // Register service worker for PWA notifications
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/sw.js")
        .then((registration) => {
          logger.info(
            "DashLayout",
            "ServiceWorker registration successful with scope: ",
            registration.scope
          );
        })
        .catch((error) => {
          logger.error(
            "DashLayout",
            "ServiceWorker registration failed: ",
            error
          );
        });
    }
  }, []);

  // Request notification permission on first load if not already set
  useEffect(() => {
    if (
      permissionChecked &&
      hasPermission === null &&
      !localStorage.getItem("permissionDialogShown")
    ) {
      // Set a session flag to prevent showing the dialog multiple times
      localStorage.setItem("permissionDialogShown", "true");
      showPermissionDialog();
    }
  }, [hasPermission, permissionChecked]);

  useEffect(() => {
    if (initialNotifications?.results) {
      dispatch(setNotifications(initialNotifications.results));
      // Already listed: a later poll must not replay them (SFRESTO-141).
      notificationPollingService.markKnown(
        initialNotifications.results.map((n) => n.id).filter(Boolean)
      );
    }
  }, [initialNotifications]);

  useEffect(() => {
    // One cleanup for every exit path: the services below are singletons, so
    // logout, a restaurant switch or leaving the page must stop them
    // (SFRESTO-142).
    let connectionMonitor: ReturnType<typeof setInterval> | null = null;
    const cleanup = () => {
      if (connectionMonitor) clearInterval(connectionMonitor);
      notificationService.disconnect();
      notificationPollingService.cleanup();
      wakeLockService.cleanup();
    };

    if (currentUser && currentRestaurant && !skipNotifications) {
      if (currentUser?.roles?.includes(Roles.admin)) {
        open();
      }
      const userRole = () => {
        const role = currentUser.roles?.filter(
          (role) => role !== Roles.user
        )?.[0];
        switch (role) {
          case Roles.manager:
            return "manager";
          case Roles.waiter:
            return "waiter";
          case Roles.kitchen:
            return "kitchen";
          case Roles.cashier:
            return "cashier";
          default:
            return "manager";
        }
      };

      // Request wake lock to prevent device sleep
      if (
        currentUser.roles?.includes(Roles.waiter) ||
        currentUser.roles?.includes(Roles.cashier) ||
        currentUser.roles?.includes(Roles.kitchen)
      ) {
        wakeLockService.request().then((success) => {
          logger.info(
            "DashLayout",
            "Wake lock request:",
            success ? "granted" : "denied"
          );
        });
      }

      const handleNotification = async (notification: any) => {
        // Add the notification to state
        dispatch(addNotification(notification));

        // Play sound for notification
        if (
          [
            "notifications.order.new_order",
            "notifications.order.customer_update",
            "notifications.order.status_changed",
          ].includes(notification.action)
        ) {
          if (
            currentUser.roles?.includes(Roles.cashier) ||
            currentUser.roles?.includes(Roles.waiter) ||
            currentUser.roles?.includes(Roles.kitchen)
          ) {
            await playNotificationSound();
          }
        }

        // Play sound for inventory notifications targeted at this user's role
        if (
          (INVENTORY_NOTIFICATION_ACTIONS as readonly string[]).includes(
            notification.action
          )
        ) {
          if (
            currentUser.roles?.includes(Roles.manager) ||
            currentUser.roles?.includes(Roles.owner) ||
            currentUser.roles?.includes(Roles.cashier)
          ) {
            await playNotificationSound();
          }
        }

        // Try to show visual notification
        try {
          if (
            "serviceWorker" in navigator &&
            Notification.permission === "granted"
          ) {
            const registration = await navigator.serviceWorker.ready;
            await registration.showNotification("Nouvelle notification", {
              body: getNotificationAction(notification),
              icon: "/favicon.ico",
              tag:
                notification.action === "notifications.order.new_order"
                  ? "new-order"
                  : (INVENTORY_NOTIFICATION_ACTIONS as readonly string[]).includes(
                      notification.action
                    )
                  ? "inventory-alert"
                  : "new-notification",
            });
          } else if (Notification.permission === "granted") {
            new Notification("Nouvelle notification", {
              body: getNotificationAction(notification),
            });
          }
        } catch (error) {
          logger.error("DashLayout", "Error showing notification:", error);
        }
      };

      try {
        // Initialize main notification service
        // Mercure deliveries move the polling cursor and are remembered, so
        // the fallback poll never replays them; duplicates are dropped
        // before any sound (SFRESTO-141).
        notificationService.init(
          currentRestaurant.id,
          userRole(),
          (notification: any) => {
            if (notification?.id) {
              if (notificationPollingService.hasSeen(notification.id)) return;
              notificationPollingService.markSeen(notification.id);
            }
            handleNotification(notification);
          }
        );

        // Initialize polling service as fallback
        notificationPollingService.init(
          currentRestaurant.id,
          userRole(),
          handleNotification
        );
        notificationPollingService.markKnown(
          (initialNotifications?.results ?? []).map((n) => n.id).filter(Boolean)
        );

        // Monitor connection status and activate polling if needed
        connectionMonitor = setInterval(() => {
          const status = notificationService.getConnectionStatus();

          if (!status.connected || !status.isServerConnectionAlive) {
            // Activate polling as fallback
            if (!notificationPollingService.isActive()) {
              logger.info(
                "DashLayout",
                "Connection issues detected, activating polling fallback"
              );
              notificationPollingService.start();
            }
          } else {
            // Connection is healthy, stop polling if active
            if (notificationPollingService.isActive()) {
              logger.info(
                "DashLayout",
                "Connection restored, deactivating polling"
              );
              notificationPollingService.stop();
            }
          }
        }, 10000); // Check every 10 seconds
      } catch (error) {
        handleSecurityError(error as QueryError, open);
      }
    }

    return cleanup;
  }, [currentUser, currentRestaurant, skipNotifications]);

  return (
    <>
      <ProtectedPage>
        <NotificationsProvider>
          <div className="flex flex-col h-screen overflow-hidden bg-background">
            {!hideTopBar && (
              <TopBar isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} />
            )}

            <div className="flex flex-1 overflow-hidden">
              <Menu
                isOpen={isMenuOpen}
                isCollapsed={isSidebarCollapsed}
                onCollapse={collapseSidebar}
              />

              <div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden">
                <Banner />
                <main className="flex-1">
                  <div className="mx-auto max-w-screen-2xl p-4 2xl:p-6">
                    {children}
                  </div>
                </main>
              </div>
            </div>

            {isSidebarCollapsed && (
              <button
                onClick={expandSidebar}
                className="hidden xl:flex fixed left-0 top-1/2 -translate-y-1/2 z-40 p-1.5 bg-white border border-l-0 border-border rounded-r-lg shadow-md text-muted-foreground hover:bg-muted transition-colors"
                aria-label="Afficher la barre latérale"
              >
                <PanelLeftOpen className="h-4 w-4" />
              </button>
            )}
          </div>
        </NotificationsProvider>
      </ProtectedPage>

      {isMenuOpen && (
        <button
          className="fixed inset-0 bg-black/50 z-20 cursor-default"
          onClick={() => setIsMenuOpen(false)}
          aria-label="Fermer le menu"
        />
      )}
      <SecurityErrorDialog isOpen={isOpen} onClose={close} />
    </>
  );
};

export default DashLayout;
